import styled from '@emotion/styled'
import { useEffect, useRef } from "react"
import { V2L2LargeModalRoot } from "../V2L2ModalRoot"
import { V2L1ModalDismissButton, V2L2ModalTitlebar } from "../V2ModalTitlebar"
import { V2L2ModalContent, V2L2ModalDetailRoot } from "../V2ModalContent"
import { V2L1ReasonableButton } from "../../Button/V2L1ReasonableButton"
import { V2L2ModalActionbar } from "../V2L2ModalActionbar"
import { faMagnifyingGlass, faPlus, faTrashCan, faXmark } from '@fortawesome/pro-solid-svg-icons'
import { FusionColor } from '../../../../theme/FusionStyle'
import L1TextField from '../../../form/l1/L1TextField'
import { ComplexElementSize } from '../../../form/ComplexElementSize'
import { V2L1LoadingIndicator } from '../../Indicator/V2L1LoadingIndicator'

const LoadingModalContent = styled(V2L2ModalContent)({
	display: 'flex',
	alignItems: 'center',
	justifyContent: 'center',
})

const DetailRoot = styled(V2L2ModalDetailRoot)({
	rowGap: '1rem',
})

const List = styled.ul(({theme}) => ({
	listStyle: 'none',
	margin: 0,
	padding: 0,
	border: `1px solid ${theme.colors.lines}`,
	borderRadius: theme.borderRadius,
	overflow: 'hidden',
}))

interface ListItemProps {
	selected?: boolean
}

const ListItem = styled.li<ListItemProps>(({theme, selected}) => ({
	display: 'grid',
	gridTemplateColumns: '1fr max-content',
	background: selected ? theme.colors.third : theme.colors.white,
	borderTop: `1px solid ${theme.colors.lines}`,
	'&:first-of-type': {
		borderTop: 'none',
	},
	padding: '0.5rem',
	alignItems: 'center',
}))

interface V2L3ListSearchModalProps {
	loading: boolean
	title: string
	itemsSelected: {title: string}[]
	itemsAvailable: {title: string, disabled?: boolean}[]
	query: string
	onQueryChange: (query: string) => void
	onAddClick: (idx: number) => void
	onDropClick: (idx: number) => void
	onDismissClick: () => void
}

export const V2L3ListSearchModal = ({
	loading,
	title,
	itemsSelected,
	itemsAvailable,
	query,
	onQueryChange,
	onAddClick,
	onDropClick,
	onDismissClick,
}: V2L3ListSearchModalProps) => {
	const fieldRef = useRef<HTMLInputElement>(null)
	useEffect(() => {
		fieldRef.current?.focus()
	}, [loading])
	if(loading) {
		return (
			<V2L2LargeModalRoot>
				<V2L2ModalTitlebar
					title={title}
					buttons={<V2L1ModalDismissButton onClick={onDismissClick} />}
				/>
				<LoadingModalContent>
					<V2L1LoadingIndicator big />
				</LoadingModalContent>
			</V2L2LargeModalRoot>
		)
	}
	return (
		<V2L2LargeModalRoot>
			<V2L2ModalTitlebar
				title={title}
				buttons={<V2L1ModalDismissButton onClick={onDismissClick} />}
			/>
			<V2L2ModalContent>
				<DetailRoot>
					{itemsSelected.length > 0 ? (
						<List>
							{itemsSelected.map((item, idx) => (
								<ListItem key={idx} selected>
									<div>{item.title}</div>
									<div>
										<V2L1ReasonableButton
											color={FusionColor.BlueMain}
											icon={faTrashCan}
											onClick={() => onDropClick(idx)}
										/>
									</div>
								</ListItem>
							))}
						</List>
					) : null}
					<L1TextField
						inputRef={fieldRef}
						prefixIcon={faMagnifyingGlass} 
						size={ComplexElementSize.FlexStretch} 
						onChange={(e) => onQueryChange(e.target.value)} 
						value={query} 
						tip={query ? (
							<V2L1ReasonableButton
								icon={faXmark}
								onClick={() => onQueryChange('')} 
							/>
						) : null}
					/>
					{itemsAvailable.length > 0 ? (
						<List>
							{itemsAvailable.map((item, idx) => (
								<ListItem key={idx}>
									<div>{item.title}</div>
									<div>
										<V2L1ReasonableButton
											color={FusionColor.BlueMain}
											icon={faPlus}
											label="Add"
											compact
											onClick={() => onAddClick(idx)}
											disabled={item.disabled}
										/>
									</div>
								</ListItem>
							))}
						</List>
					) : null}
				</DetailRoot>
			</V2L2ModalContent>
			<V2L2ModalActionbar>
				<V2L1ReasonableButton
					label="Done"
					onClick={onDismissClick}
				/>
			</V2L2ModalActionbar>
		</V2L2LargeModalRoot>
	)
}
